import ApiService from '@/core/services/api.service';
import { unMaskPrice, removeAccents, xoa_dau } from '@/utils/common';
import { Product, ChildProduct, ComboProduct } from './ProductModels';
import Brand from '../brand/models/Brand';

/**
 * Service class for handling product-related API calls and logic
 */
export class ProductService {
  static async addImagesToProduct(productId, imageIds) {
    console.log('addImagesToProduct', productId, imageIds);
    return true;
  }

  static async removeImagesFromProduct(productId, imageIds) {
    console.log('removeImagesFromProduct', productId, imageIds);
    return true;
  }

  static async getProductById(productId) {
    try {
      const response = await ApiService.get(`/products/${productId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching product by ID:', error);
      throw error;
    }
  }

  static async updateProduct(productData) {
    try {
      const response = await ApiService.put(
        `/products/${productData.id}`,
        productData,
      );
      return response.data;
    } catch (error) {
      console.error('Error updating product:', error);
      throw error;
    }
  }

  /**
   * Fetch list of categories
   * @param {Object} params - Query parameters
   * @returns {Promise} - API response
   */
  static async getCategories(params = {}) {
    const defaultParams = {
      page: 1,
      limit: 1000,
      name: '',
      code: '',
      ...params,
    };
    try {
      const { data } = await ApiService.query('categories', {
        params: defaultParams,
      });
      const listCate = [];
      const flattenCategories = (categories, level = 0) => {
        categories.forEach((element) => {
          listCate.push(element);
          if (element.children && element.children.length > 0) {
            flattenCategories(element.children, level + 1);
          }
        });
      };
      flattenCategories(data.data.list_caterogy);
      return this.formatCategories(listCate);
    } catch (error) {
      console.error('Error fetching categories:', error);
      throw error;
    }
  }

  /**
   * Format categories with proper indentation based on level
   * @param {Array} categories - List of categories
   * @returns {Array} - Formatted categories
   */
  static formatCategories(categories) {
    return categories.map((element) => {
      let name = '';
      if (element.level === 1) {
        name = '-' + element.name;
      } else if (element.level === 2) {
        name = '--' + element.name;
      } else if (element.level === 3) {
        name = '---' + element.name;
      } else if (element.level === 4) {
        name = '----' + element.name;
      } else if (element.level === 5) {
        name = '-----' + element.name;
      } else if (element.level === 6) {
        name = '------' + element.name;
      } else {
        name = element.name;
      }

      return {
        id: element.id,
        suggestionName: name,
        name: element.name,
      };
    });
  }

  /**
   * Filter categories by search text
   * @param {Array} categories - List of categories
   * @param {String} searchText - Text to search for
   * @param {Number} limit - Number of results to return
   * @returns {Array} - Filtered categories
   */
  static filterCategories(categories, searchText, limit = 10) {
    if (!searchText) {
      return [...categories];
    }

    return categories
      .filter((item) => {
        return (
          removeAccents(item.name)
            .toLowerCase()
            .indexOf(removeAccents(searchText.toLowerCase())) > -1
        );
      })
      .slice(0, limit);
  }

  /**
   * Fetch list of brands
   * @param {Object} params - Query parameters
   * @returns {Promise} - API response
   */
  static async getBrands(params = {}) {
    const defaultParams = {
      page: 1,
      pageSize: 1000,
      name: '',
      ...params,
    };

    try {
      const { data } = await ApiService.query('brands', {
        params: defaultParams,
      });
      return data.data.brands;
    } catch (error) {
      console.error('Error fetching brands:', error);
      throw error;
    }
  }

  /**
   * Filter brands by search text
   * @param {Array} brands - List of brands
   * @param {String} searchText - Text to search for
   * @param {Number} limit - Number of results to return
   * @returns {Array} - Filtered brands
   */
  static filterBrands(brands, searchText, limit = 10) {
    if (!searchText) {
      return [...brands];
    }

    return brands
      .filter((item) => {
        return (
          removeAccents(item.name)
            .toLowerCase()
            .indexOf(removeAccents(searchText.toLowerCase())) > -1
        );
      })
      .slice(0, limit);
  }

  /**
   * Create a new brand
   * @param {Object} brandData - Brand data
   * @returns {Promise} - API response
   */
  static async createBrand(brandData) {
    try {
      const brand = new Brand({
        id: brandData.id,
        name: brandData.name,
      });

      if (!brand.isValid) {
        throw new Error('Tên thương hiệu không được để trống');
      }
      const response = await ApiService.post('brands', {
        params: brand.toJSON(),
      });
      return response.data.data;
    } catch (error) {
      console.error('Error creating brand:', error);
      throw error;
    }
  }

  /**
   * Get properties by type
   * @param {Number} typeId - Property type ID
   * @returns {Promise} - API response
   */
  static async getPropertiesByType(typeId) {
    try {
      const { data } = await ApiService.get(`properties/getByType/${typeId}`);
      return data.data;
    } catch (error) {
      console.error(`Error fetching properties of type ${typeId}:`, error);
      throw error;
    }
  }

  /**
   * Create a property value
   * @param {Object} propertyData - Property data
   * @returns {Promise} - API response
   */
  static async createPropertyValue(propertyData) {
    try {
      const response = await ApiService.post(
        'propertyValue/create',
        propertyData,
      );
      return response.data;
    } catch (error) {
      console.error('Error creating property value:', error);
      throw error;
    }
  }

  /**
   * Search for products
   * @param {String} searchText - Text to search for
   * @returns {Promise} - API response
   */
  static async searchProducts(searchText) {
    try {
      const { data } = await ApiService.query('products/searchProductByName', {
        params: { name: searchText },
      });

      return data.data.map((item) => {
        item.productCode = item.productCode ? item.productCode : '';
        let suggestionName = item.productName;

        if (item.productCode !== '') {
          suggestionName = item.productCode + ' - ' + item.productName;
        }

        return {
          id: item.id,
          name: item.productName,
          code: item.productCode,
          suggestionName: suggestionName,
        };
      });
    } catch (error) {
      console.error('Error searching products:', error);
      throw error;
    }
  }

  /**
   * Create a list of products
   * @param {Array} products - List of products
   * @returns {Promise} - API response
   */
  static async createProductList(products) {
    try {
      const response = await ApiService.post('products/create-list-product', {
        listProduct: products,
      });
      return response.data;
    } catch (error) {
      console.error('Error creating products:', error);
      throw error;
    }
  }

  /**
   * Create a combo product
   * @param {Object} comboData - Combo product data
   * @returns {Promise} - API response
   */
  static async createComboProduct(comboData) {
    try {
      const response = await ApiService.post('products', comboData);
      return response.data;
    } catch (error) {
      console.error('Error creating combo product:', error);
      throw error;
    }
  }

  /**
   * Prepare product data for API submission
   * @param {Object} productInfo - Product information
   * @param {Object} form - Form validation data
   * @param {Object} options - Additional options
   * @returns {Object} - Formatted product data
   */
  static prepareProductData(productInfo, form, options = {}) {
    const {
      selectedCateId,
      selectedClassParent,
      selectedBrand,
      selectedOriginal,
      uuid,
    } = options;

    const product = new Product({
      id: uuid ? uuid() : null,
      accessories: [],
      productName: form.name ? form.name.trim() : null,
      productCode: form.code ? form.code.trim() : null,
      productCodeVat: productInfo.productCodeVat
        ? productInfo.productCodeVat.trim()
        : null,
      productNameVat: productInfo.productNameVat
        ? productInfo.productNameVat.trim()
        : null,
      productTradeName: productInfo.productTradeName
        ? productInfo.productTradeName.trim()
        : null,
      productBrandCode: productInfo.productBrandCode
        ? productInfo.productBrandCode.trim()
        : null,
      alias: productInfo.alias ? productInfo.alias.trim() : '',
      sku: productInfo.sku ? productInfo.sku.trim() : '',
      barCode: productInfo.barcode ? productInfo.barcode.trim() : null,
      parentProductId: selectedClassParent,
      productCategory: selectedCateId,
      productWebCategory: 0,
      originalPrice: productInfo.importPrice
        ? unMaskPrice(productInfo.importPrice)
        : 0,
      sellingPrice: productInfo.salePrice
        ? unMaskPrice(productInfo.salePrice)
        : 0,
      listedPrice: form.listedPrice ? unMaskPrice(form.listedPrice) : 0,
      wholesalePrice: productInfo.wholesalePrice
        ? unMaskPrice(productInfo.wholesalePrice)
        : 0,
      returnSellingPrice: 0,
      unitId: form.unitId || null,
      productType: productInfo.selectedType,
      original: selectedOriginal,
      brand: selectedBrand,
      warrantyMonthNo: productInfo.warrantyMonthNo,
      status: productInfo.selectedStatus,
      note: productInfo.note,
      productVat: form.productVat,
      imageIds: productInfo.imageIds || [],
    });

    return product.toApiData();
  }

  /**
   * Prepare child product data
   * @param {Object} parentProduct - Parent product
   * @param {Object} childProduct - Child product data
   * @param {Object} options - Additional options
   * @returns {Object} - Formatted child product data
   */
  static prepareChildProductData(parentProduct, childProduct, options = {}) {
    const { selectedCateId, uuid } = options;

    const child = ChildProduct.fromParent(parentProduct, {
      id: uuid ? uuid() : null,
      productName: childProduct.productName
        ? childProduct.productName.trim()
        : null,
      productCode: childProduct.productCode
        ? childProduct.productCode.trim()
        : null,
      barCode: childProduct.barCode ? childProduct.barCode.trim() : null,
      originalPrice: childProduct.originalPrice
        ? unMaskPrice(childProduct.originalPrice)
        : 0,
      sellingPrice: childProduct.sellingPrice
        ? unMaskPrice(childProduct.sellingPrice)
        : 0,
      productCategory: selectedCateId,
      optionValues: childProduct.optionValues,
    });

    return child.toApiData();
  }

  /**
   * Prepare combo product data
   * @param {Object} productInfo - Product information
   * @param {Object} form - Form validation data
   * @param {Array} comboItems - Combo items
   * @param {Object} options - Additional options
   * @returns {Object} - Formatted combo product data
   */
  static prepareComboProductData(productInfo, form, comboItems, options = {}) {
    const { selectedCateId, selectedOriginal, selectedBrand, uuid } = options;

    const product = new Product({
      id: uuid ? uuid() : null,
      productName: form.name,
      productCode: form.code,
      barCode: productInfo.barcode,
      parentProductId: null,
      productCategory: selectedCateId,
      productWebCategory: 0,
      originalPrice: unMaskPrice(productInfo.importPrice),
      sellingPrice: unMaskPrice(productInfo.salePrice),
      listedPrice: form.listedPrice ? unMaskPrice(form.listedPrice) : 0,
      productType: productInfo.selectedType,
      original: selectedOriginal,
      brand: selectedBrand,
      warrantyMonthNo: productInfo.warrantyMonthNo,
      status: productInfo.selectedStatus,
      note: productInfo.note,
    });

    // Prepare API data
    const apiData = product.toApiData();
    apiData.comboItems = comboItems.map((item) => {
      const comboProduct = new ComboProduct(item);
      return comboProduct.toApiData();
    });

    return apiData;
  }

  /**
   * Generate child products based on options combinations
   * @param {Object} productInfo - Product information
   * @param {Array} productOptions - Product options
   * @param {Object} options - Additional options
   * @returns {Array} - Generated child products
   */
  static generateChildProducts(productInfo, productOptions, options = {}) {
    const {
      isGenChild,
      selectedParent,
      selectedType,
      selectedClassParent,
      selectedBrand,
      selectedOriginal,
      selectedStatus,
      form,
    } = options;

    if (!isGenChild || selectedParent || selectedType === 3) {
      return [];
    }

    const validOptions = productOptions.filter(
      (option) =>
        option.name && option.optionValues && option.optionValues.length > 0,
    );

    if (validOptions.length === 0) {
      return [];
    }

    const generateCombinations = (
      options,
      currentIndex = 0,
      currentCombination = [],
      result = [],
    ) => {
      if (currentIndex === options.length) {
        result.push([...currentCombination]);
        return;
      }

      const currentOption = options[currentIndex];

      for (const value of currentOption.optionValues) {
        currentCombination.push({
          name: currentOption.name,
          value: value,
        });
        generateCombinations(
          options,
          currentIndex + 1,
          currentCombination,
          result,
        );
        currentCombination.pop();
      }

      return result;
    };

    const combinations = generateCombinations(validOptions);

    return combinations.map((combination, index) => {
      const groupedOptions = combination.reduce((acc, item) => {
        const existingOption = acc.find((opt) => opt.name === item.name);

        if (existingOption) {
          existingOption.values.push(item.value);
        } else {
          acc.push({
            name: item.name,
            values: [item.value],
          });
        }

        return acc;
      }, []);

      const variantName = combination.map((item) => item.value).join(' - ');
      const variantCode = combination
        .map((item) =>
          xoa_dau(item.value)
            .replace(/\s+/g, '')
            .substring(0, 3)
            .toUpperCase(),
        )
        .join('-');

      const childProduct = new ChildProduct({
        id: `${form.code}-${variantCode}`,
        count: index + 1,
        productName: `${form.name} - ${variantName}`,
        productCode: `${form.code}-${variantCode}`,
        productNameVat: productInfo.productNameVat
          ? productInfo.productNameVat.trim()
          : null,
        productCodeVat: productInfo.productCodeVat
          ? productInfo.productCodeVat.trim()
          : null,
        productTradeName: '',
        productBrandCode: '',
        alias: '',
        sku: '',
        parentProductId: selectedClassParent,
        barCode: '',
        original: selectedOriginal,
        status: selectedStatus,
        productType: selectedType,
        brand: selectedBrand,
        originalPrice: productInfo.importPrice
          ? unMaskPrice(productInfo.importPrice)
          : 0,
        sellingPrice: productInfo.salePrice
          ? unMaskPrice(productInfo.salePrice)
          : 0,
        wholesalePrice: productInfo.wholesalePrice
          ? unMaskPrice(productInfo.wholesalePrice)
          : 0,
        listedPrice: form.listedPrice ? unMaskPrice(form.listedPrice) : 0,
        totalQualityInstock: 0,
        productCategory: form.selectedCate,
        note: productInfo.note,
        optionValues: groupedOptions,
      });

      return childProduct;
    });
  }

  /**
   * Calculate sum of prices for combo products
   * @param {Array} comboProducts - List of combo products
   * @returns {Number} - Sum of prices
   */
  static calculateComboSum(comboProducts) {
    return comboProducts.reduce((sum, product) => {
      return (
        sum +
        (product.productComboPrice ? unMaskPrice(product.productComboPrice) : 0)
      );
    }, 0);
  }

  /**
   * Fetch product history by product ID
   * @param {Number} productId - Product ID
   * @returns {Promise} - API response
   */
  static async fetchProductHistory(productId) {
    try {
      const response = await ApiService.get(`products/histories/${productId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching product history:', error);
      throw error;
    }
  }

  static async downloadImportTemplate() {
    try {
      const response = await ApiService.get('/products/import-template', {
        responseType: 'blob',
      });
      return response;
    } catch (error) {
      console.error('Error downloading import template:', error);
      throw error;
    }
  }
}
